import { lumi } from "@/lib/lumi";
import {
  candidateMatchesAlert,
  type CandidateAlert,
} from "@/hooks/useCandidateAlerts";
import {
  formatProfileLabel,
  MOBILITY_LABELS,
  HOUSING_LABELS,
  type AvailableCandidate,
} from "@/hooks/useAvailableCandidates";
import { emailRecruiter_CandidateAlertMatches } from "@/lib/emails";

/**
 * Service de notification des alertes "Candidat disponible immédiatement"
 * côté recruteur.
 *
 * À la connexion d'un recruteur, on parcourt ses alertes actives et, pour chaque
 * alerte, on regarde s'il y a de nouveaux profils `available_candidates`
 * (publiés, en cours de validité 48h) qui correspondent aux critères et qui
 * n'ont pas déjà été notifiés (`lastNotifiedCandidateIds`).
 *
 * Si oui :
 *  - on envoie un email récap via `emailRecruiter_CandidateAlertMatches`
 *  - on met à jour l'alerte avec `lastSentAt` et `lastNotifiedCandidateIds`
 *
 * Best-effort : tout échec est loggé sans bloquer l'application.
 *
 * Fréquences gérées :
 *  - "immediate" : déclenche dès qu'un profil match (cooldown anti-spam de 5 min)
 *  - "daily"     : fenêtre 24h, cooldown 1h
 *  - "weekly"    : fenêtre 7 jours, cooldown 1h
 */

const STORAGE_KEY_LAST_CHECK = "bnssa-mns:candidate-alerts:last-check";

/**
 * Fenêtre de fraîcheur pour considérer un profil comme "nouveau".
 * On l'aligne sur la durée de visibilité d'un profil disponible immédiatement (48h)
 * pour la fréquence "immediate" : tous les profils encore visibles publiquement
 * doivent pouvoir déclencher une notification au moins une fois (la dédup se fait
 * via `lastNotifiedCandidateIds`).
 */
function freshnessWindowMs(frequency: CandidateAlert["frequency"]): number {
  if (frequency === "immediate") return 48 * 60 * 60 * 1000; // 48h, identique à la fenêtre de dispo
  if (frequency === "daily") return 24 * 3600 * 1000;
  return 7 * 24 * 3600 * 1000;
}

/** Cooldown client minimum entre deux exécutions (anti-spam navigateur). */
function cooldownMs(frequency: CandidateAlert["frequency"]): number {
  // Cooldown court en "immediate" pour permettre la détection rapide d'un nouveau profil
  return frequency === "immediate" ? 5 * 60 * 1000 : 60 * 60 * 1000;
}

function canRunNow(): boolean {
  if (typeof window === "undefined") return false;
  try {
    const last = window.localStorage.getItem(STORAGE_KEY_LAST_CHECK);
    if (!last) return true;
    const t = parseInt(last, 10);
    if (Number.isNaN(t)) return true;
    // Cooldown global minimal de 2 min pour ne pas spammer le client.
    return Date.now() - t > 2 * 60 * 1000;
  } catch {
    return true;
  }
}

function markRun() {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(STORAGE_KEY_LAST_CHECK, String(Date.now()));
  } catch {
    // silent
  }
}

export async function checkAndNotifyCandidateAlerts(opts: {
  userId: string;
  userEmail?: string;
  userName?: string;
}): Promise<void> {
  if (!opts.userId || !opts.userEmail) return;
  if (!canRunNow()) {
    console.log(
      "[candidateAlerts] Skipped — déjà exécuté il y a moins de 2 minutes"
    );
    return;
  }

  try {
    // 1) Charger les alertes actives du recruteur
    const { list: alertsRaw } = await lumi.entities.candidate_alerts.list({
      filter: { creator: opts.userId, isActive: true },
    });
    const alerts = (alertsRaw || []) as CandidateAlert[];
    if (alerts.length === 0) {
      markRun();
      return;
    }

    // 2) Charger les candidats disponibles immédiatement, fenêtre 48h
    const nowIso = new Date().toISOString();
    const { list: candidatesRaw } = await lumi.entities.available_candidates.list({
      filter: {
        isPublished: true,
        isAvailableNow: true,
        availableUntil: { $gte: nowIso },
      },
      sort: { updatedAt: -1 },
      limit: 200,
    });
    const candidates = (candidatesRaw || []) as AvailableCandidate[];
    if (candidates.length === 0) {
      markRun();
      return;
    }

    let anyEmailSent = false;

    // 3) Pour chaque alerte, filtrer les candidats éligibles
    for (const alert of alerts) {
      try {
        const windowMs = freshnessWindowMs(alert.frequency);
        const cooldown = cooldownMs(alert.frequency);

        // Cooldown par alerte (basé sur lastSentAt en DB)
        if (alert.lastSentAt) {
          const ms = Date.now() - new Date(alert.lastSentAt).getTime();
          if (!Number.isNaN(ms) && ms < cooldown) {
            console.log(
              `[candidateAlerts] Skipped alerte "${alert.name}" — cooldown actif (${Math.round(ms / 1000)}s < ${Math.round(cooldown / 1000)}s)`
            );
            continue;
          }
        }

        const sinceTs = alert.lastSentAt
          ? new Date(alert.lastSentAt).getTime()
          : Date.now() - windowMs;
        const alreadyNotified = new Set(alert.lastNotifiedCandidateIds || []);

        let rejectedNotified = 0;
        let rejectedFreshness = 0;
        let rejectedCriteria = 0;

        const matches = candidates.filter((c) => {
          if (alreadyNotified.has(c._id)) {
            rejectedNotified++;
            return false;
          }
          const refIso =
            c.availableSince ||
            (c as { updatedAt?: string }).updatedAt ||
            (c as { createdAt?: string }).createdAt ||
            "";
          const ts = refIso ? new Date(refIso).getTime() : 0;
          if (ts < sinceTs) {
            rejectedFreshness++;
            return false;
          }
          const ok = candidateMatchesAlert(
            {
              qualification: c.qualification,
              city: c.city,
              region: c.region,
              mobility: c.mobility,
              housingNeed: c.housingNeed,
              ...(c as { mobilityDepartment?: string; specialty?: string }),
            } as Parameters<typeof candidateMatchesAlert>[0],
            alert
          );
          if (!ok) rejectedCriteria++;
          return ok;
        });

        console.log(
          `[candidateAlerts] Alerte "${alert.name}" → matches=${matches.length} (déjà notifiés: ${rejectedNotified}, hors fenêtre: ${rejectedFreshness}, critères KO: ${rejectedCriteria}, candidats analysés: ${candidates.length})`
        );

        if (matches.length === 0) continue;
        anyEmailSent = true;

        // 4) Envoyer l'email récap
        await emailRecruiter_CandidateAlertMatches({
          recruiterEmail: opts.userEmail,
          recruiterName: opts.userName,
          alertName: alert.name,
          candidates: matches.map((c) => ({
            id: c._id,
            label: formatProfileLabel(c.qualification, c.publicId),
            qualification: c.qualification,
            city: c.city || "—",
            region: c.region || "—",
            mobility: MOBILITY_LABELS[c.mobility] || c.mobility,
            housingNeed: HOUSING_LABELS[c.housingNeed] || c.housingNeed,
            availableUntil: c.availableUntil,
          })),
        });

        // 5) Mettre à jour l'alerte
        const newNotifiedIds = Array.from(
          new Set([
            ...(alert.lastNotifiedCandidateIds || []),
            ...matches.map((m) => m._id),
          ])
        ).slice(-200);

        await lumi.entities.candidate_alerts.update(alert._id, {
          lastSentAt: new Date().toISOString(),
          lastNotifiedCandidateIds: newNotifiedIds,
          updatedAt: new Date().toISOString(),
        });

        console.log(
          `[candidateAlerts] Email envoyé pour l'alerte "${alert.name}" — ${matches.length} candidat(s)`
        );
      } catch (innerErr) {
        console.warn(
          "[candidateAlerts] Échec sur une alerte",
          alert._id,
          innerErr
        );
      }
    }

    if (anyEmailSent) markRun();
  } catch (err) {
    console.warn(
      "[candidateAlerts] Échec global du check des alertes candidats",
      err
    );
  }
}

/**
 * Déclenché lorsqu'un CANDIDAT publie / met à jour sa disponibilité.
 *
 * Contrairement à `checkAndNotifyCandidateAlerts` (qui ne scanne que les alertes
 * du recruteur connecté), cette fonction parcourt TOUTES les alertes candidats
 * actives de TOUS les recruteurs et notifie ceux dont les critères correspondent
 * au profil qui vient d'être publié — sans dépendre d'aucune connexion recruteur
 * ni du cron serveur.
 *
 * Best-effort : tout échec est loggé sans bloquer l'application.
 */
export async function notifyRecruitersOnCandidatePublished(opts: {
  candidate: AvailableCandidate;
}): Promise<void> {
  const c = opts.candidate;
  // On ne notifie que pour un profil publié et disponible immédiatement (48h).
  if (!c || c.isPublished === false || c.isAvailableNow !== true) {
    console.log(
      "[candidateAlerts] notifyRecruitersOnCandidatePublished — profil non éligible (non publié ou non dispo immédiate), skip"
    );
    return;
  }

  try {
    // 1) Charger TOUTES les alertes candidats actives (tous recruteurs)
    const { list: alertsRaw } = await lumi.entities.candidate_alerts.list({
      filter: { isActive: true },
      limit: 500,
    });
    const alerts = (alertsRaw || []) as CandidateAlert[];
    if (alerts.length === 0) {
      console.log("[candidateAlerts] Aucune alerte candidat active, rien à notifier");
      return;
    }

    const candidateInput = {
      qualification: c.qualification,
      city: c.city,
      region: c.region,
      mobility: c.mobility,
      housingNeed: c.housingNeed,
      ...(c as { mobilityDepartment?: string; specialty?: string }),
    } as Parameters<typeof candidateMatchesAlert>[0];

    for (const alert of alerts) {
      try {
        // Dédup : ne pas renotifier si ce candidat a déjà été signalé
        const alreadyNotified = new Set(alert.lastNotifiedCandidateIds || []);
        if (alreadyNotified.has(c._id)) {
          continue;
        }
        // Filtrage critères
        if (!candidateMatchesAlert(candidateInput, alert)) {
          continue;
        }
        const recruiterEmail = alert.creatorEmail;
        if (!recruiterEmail) {
          console.warn(
            `[candidateAlerts] Alerte "${alert.name}" sans creatorEmail — impossible de notifier`
          );
          continue;
        }

        await emailRecruiter_CandidateAlertMatches({
          recruiterEmail,
          recruiterName: alert.name,
          alertName: alert.name,
          candidates: [
            {
              id: c._id,
              label: formatProfileLabel(c.qualification, c.publicId),
              qualification: c.qualification,
              city: c.city || "—",
              region: c.region || "—",
              mobility: MOBILITY_LABELS[c.mobility] || c.mobility,
              housingNeed: HOUSING_LABELS[c.housingNeed] || c.housingNeed,
              availableUntil: c.availableUntil,
            },
          ],
        });

        const newNotifiedIds = Array.from(
          new Set([...(alert.lastNotifiedCandidateIds || []), c._id])
        ).slice(-200);

        await lumi.entities.candidate_alerts.update(alert._id, {
          lastSentAt: new Date().toISOString(),
          lastNotifiedCandidateIds: newNotifiedIds,
          updatedAt: new Date().toISOString(),
        });

        console.log(
          `[candidateAlerts] Recruteur notifié pour l'alerte "${alert.name}" (profil ${c.publicId})`
        );
      } catch (innerErr) {
        console.warn(
          "[candidateAlerts] Échec notification d'une alerte recruteur",
          alert._id,
          innerErr
        );
      }
    }
  } catch (err) {
    console.warn(
      "[candidateAlerts] Échec global notifyRecruitersOnCandidatePublished",
      err
    );
  }
}

/**
 * Diagnostic d'un échec de match : retourne la liste des critères qui rejettent
 * un candidat donné par rapport à une alerte. Utile pour expliquer au recruteur
 * pourquoi tel candidat ne passe pas le filtre.
 */
function diagnoseRejection(
  candidate: AvailableCandidate,
  alert: CandidateAlert
): string[] {
  const reasons: string[] = [];
  const c = candidate as AvailableCandidate & {
    mobilityDepartment?: string;
    specialty?: string;
  };

  if (alert.qualification && alert.qualification !== "ALL") {
    if (c.qualification !== alert.qualification) {
      reasons.push(`diplôme ${c.qualification} ≠ ${alert.qualification}`);
    }
  }
  if (alert.region && alert.region.trim()) {
    const needle = alert.region.trim().toLowerCase();
    if (!(c.region || "").toLowerCase().includes(needle)) {
      reasons.push(`région "${c.region || "—"}" ≠ "${alert.region}"`);
    }
  }
  // Compat ascendante (anciennes alertes avec critères avancés) :
  if (alert.department && alert.department.trim()) {
    const needle = alert.department.trim().toLowerCase();
    const explicit = (c.mobilityDepartment || "").toString().toLowerCase();
    const fallback = `${c.city || ""} ${c.region || ""}`.toLowerCase();
    const hay = `${explicit} ${fallback}`.trim();
    if (hay.length > 0 && !hay.includes(needle)) {
      reasons.push(`département "${alert.department}" introuvable côté candidat`);
    }
  }
  if (alert.city && alert.city.trim()) {
    const needle = alert.city.trim().toLowerCase();
    if (!(c.city || "").toLowerCase().includes(needle)) {
      reasons.push(`ville "${c.city || "—"}" ≠ "${alert.city}"`);
    }
  }
  if (alert.mobility && alert.mobility !== "any") {
    // simple message générique, le ranking est complexe
    reasons.push(`mobilité candidat (${c.mobility}) < min "${alert.mobility}"`);
  }
  if (alert.housingNeed && alert.housingNeed !== "any") {
    if (alert.housingNeed === "non" && c.housingNeed !== "non") {
      reasons.push(`logement : candidat="${c.housingNeed}" alors que l'alerte demande "non"`);
    }
    if (
      alert.housingNeed === "oui" &&
      c.housingNeed !== "oui" &&
      c.housingNeed !== "oui_si_eloigne"
    ) {
      reasons.push(`logement : candidat="${c.housingNeed}" alors que l'alerte demande "oui"`);
    }
  }
  if (alert.specialty && alert.specialty.trim()) {
    const candidateSpecialty = (c.specialty || "").toString().trim().toLowerCase();
    if (candidateSpecialty.length > 0) {
      const needle = alert.specialty.trim().toLowerCase();
      if (!candidateSpecialty.includes(needle)) {
        reasons.push(`spécialité "${candidateSpecialty}" ≠ "${alert.specialty}"`);
      }
    }
  }
  return reasons;
}

export interface TestAlertResult {
  totalCandidates: number;
  matched: number;
  emailSent: boolean;
  emailError?: string;
  /** Premières raisons de rejet, pour faciliter le diagnostic. */
  topRejections: string[];
}

/**
 * Test à la demande d'une alerte donnée. Bypass : cooldown global,
 * cooldown par alerte, dédup `lastNotifiedCandidateIds`, fenêtre de fraîcheur.
 *
 * Retourne un diagnostic détaillé pour informer le recruteur.
 */
export async function testCandidateAlertNow(opts: {
  alert: CandidateAlert;
  recruiterEmail: string;
  recruiterName?: string;
}): Promise<TestAlertResult> {
  const result: TestAlertResult = {
    totalCandidates: 0,
    matched: 0,
    emailSent: false,
    topRejections: [],
  };

  const nowIso = new Date().toISOString();
  const { list: candidatesRaw } = await lumi.entities.available_candidates.list({
    filter: {
      isPublished: true,
      isAvailableNow: true,
      availableUntil: { $gte: nowIso },
    },
    sort: { updatedAt: -1 },
    limit: 200,
  });
  const candidates = (candidatesRaw || []) as AvailableCandidate[];
  result.totalCandidates = candidates.length;

  if (candidates.length === 0) {
    result.topRejections = ["Aucun candidat publié et disponible pour le moment."];
    return result;
  }

  const matches: AvailableCandidate[] = [];
  const rejectionReasonsAcc: string[] = [];

  for (const c of candidates) {
    const ok = candidateMatchesAlert(
      {
        qualification: c.qualification,
        city: c.city,
        region: c.region,
        mobility: c.mobility,
        housingNeed: c.housingNeed,
        ...(c as { mobilityDepartment?: string; specialty?: string }),
      } as Parameters<typeof candidateMatchesAlert>[0],
      opts.alert
    );
    if (ok) {
      matches.push(c);
    } else {
      const reasons = diagnoseRejection(c, opts.alert);
      if (reasons.length > 0) {
        const label = formatProfileLabel(c.qualification, c.publicId);
        rejectionReasonsAcc.push(`${label}: ${reasons.join(", ")}`);
      }
    }
  }
  result.matched = matches.length;
  result.topRejections = rejectionReasonsAcc.slice(0, 5);

  if (matches.length === 0) {
    return result;
  }

  // Envoi de l'email même en mode test
  try {
    await emailRecruiter_CandidateAlertMatches({
      recruiterEmail: opts.recruiterEmail,
      recruiterName: opts.recruiterName,
      alertName: `${opts.alert.name} (test)`,
      candidates: matches.map((c) => ({
        id: c._id,
        label: formatProfileLabel(c.qualification, c.publicId),
        qualification: c.qualification,
        city: c.city || "—",
        region: c.region || "—",
        mobility: MOBILITY_LABELS[c.mobility] || c.mobility,
        housingNeed: HOUSING_LABELS[c.housingNeed] || c.housingNeed,
        availableUntil: c.availableUntil,
      })),
    });
    result.emailSent = true;
    // On met aussi à jour lastSentAt pour cohérence avec un envoi normal
    await lumi.entities.candidate_alerts.update(opts.alert._id, {
      lastSentAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    });
  } catch (e) {
    console.error("[candidateAlerts] testCandidateAlertNow email error", e);
    result.emailError = (e as Error)?.message || "Erreur d'envoi inconnue";
  }

  return result;
}
